import React from "react";
import { Helmet } from "react-helmet-async";

// Canonical site URL. Production = freeaiwriters.com
// Override via env if needed: REACT_APP_SITE_URL
const SITE_URL = (
    process.env.REACT_APP_SITE_URL || "https://freeaiwriters.com"
).replace(/\/+$/, "");

/**
 * Reusable SEO meta block.
 * Always sets:
 *   - <title>
 *   - <meta name="description">
 *   - <link rel="canonical">  (self-referencing to current page)
 *   - Open Graph + Twitter card
 *
 * Pass `path` as the relative path like "/", "/tool/ai-blog-writer", "/category/article-content".
 */
export default function Seo({
    title,
    description,
    path = "/",
    image = `${SITE_URL}/apple-touch-icon.png`,
    type = "website",
    noindex = false,
}) {
    // Strip trailing slash (except for root) per user requirement
    let clean = path || "/";
    if (clean.length > 1 && clean.endsWith("/")) clean = clean.replace(/\/+$/, "");
    const url = `${SITE_URL}${clean}`;

    return (
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={url} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}
            {!noindex && (
                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
                />
            )}

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content="Free AI Writers" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={image} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
        </Helmet>
    );
}

export { SITE_URL };
